import { z } from '@customerio/hydra-validation';
import type { EnvironmentsGetEnvironmentResponse } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';
import type { Workspace } from '@/services/auth/types';

/**
 * Workspace environment — a per-workspace singleton aggregate. The
 * `GET /environments/{id}` response bundles the environment record with its
 * sideloaded `identities` and `object_types`, and consumers read all three,
 * so the entity wraps the whole response rather than just the bare
 * `Environment` model.
 *
 * Minimal detail-only entity: the read schema is a passthrough today (no
 * field needs normalization for current consumers). A list/mutation surface
 * can be added when needed.
 */
export type WorkspaceEnvironment = EnvironmentsGetEnvironmentResponse;

const EnvironmentFromApi = readSchema<WorkspaceEnvironment>(z.looseObject({}));

/** @public */
export const environment = defineEntity<WorkspaceEnvironment>({
  name: 'environment',
  scope: 'workspace',
  // One environment per workspace; key by its own id when present.
  pk: (env) => String(env.environment?.id ?? 'current'),
  schema: { read: EnvironmentFromApi },
});

export const environmentKeys = {
  ...environment.keys,
  /** Singleton slot for the workspace's environment aggregate. */
  current: (workspaceId: number) => [...environment.keys.all(workspaceId), 'current'] as const,
  /** The workspace's usable tracking site ids. */
  siteIds: (workspaceId: number) => [...environment.keys.all(workspaceId), 'site-ids'] as const,
  /** Every account's workspace list. Account-scoped, so it sits outside any one workspace's keys. */
  accountLists: () => ['environment', 'account-list'] as const,
  /** The account's workspaces, plus the sample workspace when the user can see one. */
  accountList: (accountId: number, sample: SampleWorkspaceRef | null) =>
    [...environmentKeys.accountLists(), accountId, sample] as const,
  /** The key an inactive read parks on. Never written. */
  inactive: (read: string) => ['environment', 'inactive', read] as const,
};

export function parseEnvironment(raw: EnvironmentsGetEnvironmentResponse): WorkspaceEnvironment {
  return environment.parse(raw);
}

export const DeliverySettings = {
  Normal: 'normal',
  SendToOne: 'send_to_one',
  Off: 'off',
} as const;
export type DeliverySettings = (typeof DeliverySettings)[keyof typeof DeliverySettings];

/** Identifier-state values (`IdentifierState`) keyed by identifier. */
export interface WorkspaceIdentifiers {
  cio_id: number;
  id: number;
  email: number;
  phone?: number;
}

/**
 * A workspace as the workspace list shows it: the account's environment record
 * with the Journeys and Pipelines ids, which are the same workspace id.
 */
export interface MergedWorkspace {
  id: number;
  account_id: number;
  cio_workspace_id: number;
  cdp_workspace_id: number;
  cio_initialized: boolean;
  cdp_initialized: boolean;
  name: string;
  color: string;
  /**
   * One of `DeliverySettings` in practice, but passed through as the backend sent it: an empty
   * value means delivery was never set up (onboarding reads it that way), and the workspaces
   * table shows any other value as unknown.
   */
  delivery_settings: string;
  delivery_email: string;
  email_open_tracking: boolean;
  identifier_type: string;
  identifiers?: WorkspaceIdentifiers;
  user_ids: string[];
}

/** The sample workspace a user can explore, and the sample account it lives in. */
export interface SampleWorkspaceRef {
  accountId: number;
  workspaceId: number;
}

/** Maps the account payload's environment record onto the list's workspace shape. */
export function parseAccountWorkspace(workspace: Workspace): MergedWorkspace {
  const workspaceId = Number(workspace.id);
  return {
    id: workspaceId,
    account_id: Number(workspace.account_id),
    cio_workspace_id: workspaceId,
    cdp_workspace_id: workspaceId,
    cio_initialized: true,
    cdp_initialized: true,
    name: workspace.name,
    color: workspace.hex_code ?? '',
    delivery_settings: workspace.delivery_settings,
    delivery_email: workspace.delivery_email,
    // Default to on (the Ember model default) so a workspace whose payload omits the field isn't
    // shown as "don't track opens" by the falsy check in the list's delivery column.
    email_open_tracking: workspace.email_open_tracking ?? true,
    identifier_type: workspace.identifier_type,
    identifiers: workspace.identifiers,
    user_ids: workspace.user_ids ?? [],
  };
}

/** The read-only sample workspace, added to the list for users who can explore it. */
export function sampleAccountWorkspace({ accountId, workspaceId }: SampleWorkspaceRef): MergedWorkspace {
  return {
    id: workspaceId,
    account_id: accountId,
    cio_workspace_id: workspaceId,
    cdp_workspace_id: workspaceId,
    cio_initialized: true,
    cdp_initialized: true,
    name: 'Sample Workspace',
    color: '#000000',
    delivery_settings: DeliverySettings.Normal,
    delivery_email: '',
    email_open_tracking: true,
    identifier_type: 'email',
    identifiers: undefined,
    user_ids: [],
  };
}
